import {
	ApiPlatformEnum,
	EcommerceCartDataSourceEnum,
	EcommerceShoppingListDataSourceEnum
} from '@inter-ikea-kompis/enums';
import type {
	IDexfSettingsKompisLocalization,
	IDexfSettingsKompisServiceSettings
} from '@inter-ikea-kompis/types';

import type IShoppingSupportLocalizedInformation from './IShoppingSupportLocalizedInformation';

export type IShoppingSupportDexfSettings = {
	kompis: {
		serviceSettings: Pick<
			IDexfSettingsKompisServiceSettings,
			'eCommerceCartDataSource' | 'apiPlatform' | 'eCommerceShoppingListDataSource'
		>;
		localization: Pick<IDexfSettingsKompisLocalization, 'shopOnlineEnabled'>;
	};
};

type ShoppingSupportSettings = IShoppingSupportLocalizedInformation | IShoppingSupportDexfSettings;

/**
 * A utility for checking for shopping cart and list support.
 */
export default class ShoppingSupport {
	/**
	 * Checks if shopping cart is supported.
	 *
	 * @param priceSettingsOrDexfPriceSettings
	 * @returns Returns "true" if shopping cart is supported.
	 */
	public static hasCartSupport(priceSettingsOrDexfPriceSettings: ShoppingSupportSettings): boolean {
		const localization = this.unwrapDexfSettingsObject(priceSettingsOrDexfPriceSettings);

		const eCommerceCartDataSource = localization?.eCommerceCartDataSource;
		const shopOnlineEnabled = localization?.shopOnlineEnabled;

		switch (eCommerceCartDataSource) {
			case EcommerceCartDataSourceEnum.disabled:
				return false;
			case EcommerceCartDataSourceEnum.default:
				return shopOnlineEnabled === true;
		}

		return true;
	}

	/**
	 * Checks if shopping list is supported.
	 *
	 * @param priceSettingsOrDexfPriceSettings
	 * @returns Returns "true" if shopping list is supported.
	 */
	public static hasListSupport(priceSettingsOrDexfPriceSettings: ShoppingSupportSettings): boolean {
		const localization = this.unwrapDexfSettingsObject(priceSettingsOrDexfPriceSettings);

		const apiPlatform = localization?.apiPlatform;
		const eCommerceShoppingListDataSource = localization?.eCommerceShoppingListDataSource;
		const eCommerceCartDataSource = localization?.eCommerceCartDataSource;

		switch (eCommerceShoppingListDataSource) {
			case EcommerceShoppingListDataSourceEnum.shoppingListApi:
			case EcommerceShoppingListDataSourceEnum.chinaShoppingListApi:
			case EcommerceShoppingListDataSourceEnum.iows:
			case EcommerceShoppingListDataSourceEnum.mvecom:
			case EcommerceShoppingListDataSourceEnum.iksa:
				return true;
			case EcommerceShoppingListDataSourceEnum.default:
				switch (eCommerceCartDataSource) {
					case EcommerceCartDataSourceEnum.iows:
					case EcommerceCartDataSourceEnum.cartApi:
						return true;
					case EcommerceCartDataSourceEnum.default:
						switch (apiPlatform) {
							case ApiPlatformEnum.irw:
								return true;
						}
				}
		}

		return false;
	}

	/**
	 * Constructs an object allowing DEXFSettings and LocalizedInformation to be passed into the function.
	 *
	 * @param priceSettingsOrDexfPriceSettings
	 * @returns Returns the object with all properties required for ShoppingSupport.
	 */
	private static unwrapDexfSettingsObject(
		priceSettingsOrDexfPriceSettings: ShoppingSupportSettings
	): IShoppingSupportLocalizedInformation['localization'] {
		if ('kompis' in priceSettingsOrDexfPriceSettings) {
			const { serviceSettings, localization } = priceSettingsOrDexfPriceSettings.kompis;

			return {
				eCommerceCartDataSource: serviceSettings.eCommerceCartDataSource,
				apiPlatform: serviceSettings.apiPlatform,
				eCommerceShoppingListDataSource: serviceSettings.eCommerceShoppingListDataSource,
				shopOnlineEnabled: localization.shopOnlineEnabled
			};
		} else {
			const localization = priceSettingsOrDexfPriceSettings.localization;

			return {
				eCommerceCartDataSource: localization?.eCommerceCartDataSource,
				apiPlatform: localization?.apiPlatform,
				eCommerceShoppingListDataSource: localization?.eCommerceShoppingListDataSource,
				shopOnlineEnabled: localization?.shopOnlineEnabled
			};
		}
	}
}
